import {
  AbsenceInfo,
  ScheduleCalendarType,
  ScheduleDataContextType,
  ScheduleDataModel,
} from '../types';
import {
  getMinViewModelPeriod,
  getShiftsWithTasksAndUnassignedTasks,
} from '../internals';
import {
  useAbsences,
  useAbsenceTypes,
  useShifts,
  useTasks,
  useTeamMembers,
} from '../../../entities';
import {useMemo} from 'react';
import {getMergedTasksWithEditState} from '../functions';
import {useEditScheduleDataContext} from './useEditScheduleDataContext';

/**
 * @param teamId
 * @param date
 * @param calendarType
 * @returns - all shifts and tasks for team for selected date's month(s), to derive the following
 *            => Team member workload for month of selected date
 *            => Team member shifts with tasks for selected period (date/type), possibly several months
 *            => All unassigned tasks for ditto
 *            => Daily totals for ditto
 */
export const useScheduleDataContextProvider = (
  teamId: number,
  date: Date,
  calendarType: ScheduleCalendarType,
): ScheduleDataContextType => {
  // Get extended date range
  const {fromDate, toDate} = useMemo(
    () => getMinViewModelPeriod(date, calendarType),
    [calendarType, date],
  );

  // Get data
  const members = useTeamMembers(teamId);
  const tasks = useTasks(t => t.distrDate >= fromDate && t.distrDate <= toDate);
  const shifts = useShifts(
    s =>
      s.teamId === teamId && s.distrDate >= fromDate && s.distrDate <= toDate,
  );
  const absences = useAbsences();
  const absenceTypes = useAbsenceTypes();

  // Edit state
  const {editState, ...editOperations} = useEditScheduleDataContext(tasks);

  // Get all shifts and tasks
  const {shiftsWithTasks, unassignedTasks} = useMemo(() => {
    return getShiftsWithTasksAndUnassignedTasks(
      getMergedTasksWithEditState(editState, tasks),
      shifts,
      absences,
    );
  }, [editState, tasks, shifts, absences]);

  // Create the viewmodel
  const viewmodel = useMemo(
    (): ScheduleDataModel => ({
      shiftsWithTasks,
      members,
      absences: absences.map(absence => ({
        absence,
        absenceType: absenceTypes.find(p => p.id === absence.typeId),
      })) as AbsenceInfo[],
      unassignedTasks: unassignedTasks.filter(
        t => t.distrDate >= fromDate && t.distrDate <= toDate,
      ),
    }),
    [
      shiftsWithTasks,
      members,
      absences,
      unassignedTasks,
      absenceTypes,
      fromDate,
      toDate,
    ],
  );

  return useMemo(
    () => ({
      ...viewmodel,
      teamId,
      date,
      calendarType,
      dates: {
        totalRange: {
          fromDate,
          toDate,
        },
      },
      ...editOperations,
      editState,
      shiftsWithTasks,
    }),
    [
      viewmodel,
      teamId,
      date,
      calendarType,
      fromDate,
      toDate,
      editOperations,
      editState,
      shiftsWithTasks,
    ],
  );
};
